import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { dashboardAPI } from '../services/api';
import { isInfluencerUser } from '../utils/auth';
import './Dashboard.css';

const CHART_COLORS = ['#3D2031', '#8A4D61', '#5B2D46', '#c4a5b0', '#b08996', '#6b7280', '#9ca3af'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const charts = stats?.charts || {};

  const ordersChartData = useMemo(() => {
    const rows = charts.orders_by_day || [];
    return rows.map((r) => ({
      ...r,
      label: String(r.date || '').slice(5),
    }));
  }, [charts.orders_by_day]);

  const revenueChartData = useMemo(() => {
    const rows = charts.revenue_by_day || [];
    return rows.map((r) => ({
      ...r,
      label: String(r.date || '').slice(5),
    }));
  }, [charts.revenue_by_day]);

  const productStatusPie = charts.products_by_status || [];
  const orderStatusPie = charts.orders_by_status || [];

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num || 0);

  const safePercent = (part, total) => {
    if (!total || total <= 0) return '0.0';
    return ((part / total) * 100).toFixed(1);
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="alert alert-error">Failed to load dashboard data</div>;
  }

  const isCloset = (stats.scope || 'platform') === 'closet';
  const isInfluencer = isInfluencerUser();

  const quickActions = isInfluencer
    ? [
        { to: '/products', label: 'My products', hint: 'Manage listings', highlight: true },
        { to: '/orders', label: 'Orders', hint: 'Closet sales' },
        { to: '/products/new', label: 'Add product', hint: 'New listing' },
      ]
    : [
        { to: '/products', label: 'Products', hint: `${formatNumber(stats.pending_products)} pending`, highlight: stats.pending_products > 0 },
        { to: '/orders', label: 'Orders', hint: `${formatNumber(stats.pending_orders)} pending`, highlight: stats.pending_orders > 0 },
        { to: '/products/new', label: 'New product', hint: 'Create listing' },
        { to: '/customers', label: 'Customers', hint: formatNumber(stats.total_customers) },
        { to: '/settings', label: 'Settings', hint: 'Store config' },
      ];

  const healthCards = [
    {
      title: 'Approval rate',
      value: `${safePercent(stats.approved_products, stats.total_products)}%`,
      subtitle: `${formatNumber(stats.approved_products)} of ${formatNumber(stats.total_products)} listings`,
    },
    {
      title: 'Order fulfillment',
      value: `${safePercent(stats.delivered_orders, stats.total_orders)}%`,
      subtitle: `${formatNumber(stats.delivered_orders)} delivered`,
    },
    isCloset
      ? {
          title: 'Engagement',
          value: formatNumber(
            (stats.total_chats || 0) + (stats.total_wishlist_items || 0) + (stats.total_product_likes || 0),
          ),
          subtitle: `Chats + saves + likes`,
        }
      : {
          title: 'User activation',
          value: `${safePercent(stats.active_users, stats.total_users)}%`,
          subtitle: `${formatNumber(stats.active_users)} active users`,
        },
    {
      title: 'Pending workload',
      value: formatNumber((stats.pending_orders || 0) + (stats.pending_products || 0)),
      subtitle: `${formatNumber(stats.pending_orders)} orders · ${formatNumber(stats.pending_products)} listings`,
    },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-quick-actions">
        {quickActions.map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className={`dashboard-quick-action ${action.highlight ? 'highlight' : ''}`}
          >
            <strong>{action.label}</strong>
            <span>{action.hint}</span>
          </Link>
        ))}
      </div>

      <div className="dashboard-top">
        <div className="dashboard-hero">
          <h3>{isCloset ? 'Your closet snapshot' : 'Platform snapshot'}</h3>
          <p>
            {isCloset
              ? 'Products, orders, and trends for your closet only.'
              : 'Operational overview with trends for moderation and growth.'}
          </p>
          <div className="dashboard-hero-meta">
            <span>Paid revenue: {formatCurrency(stats.total_revenue)}</span>
            <span>Orders: {formatNumber(stats.total_orders)}</span>
            <span>Listings: {formatNumber(stats.total_products)}</span>
          </div>
        </div>
        <div className="dashboard-kpi-grid">
          {healthCards.map((card) => (
            <div className="dashboard-kpi-card" key={card.title}>
              <p>{card.title}</p>
              <h4>{card.value}</h4>
              <span>{card.subtitle}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-charts-section">
        <h3 className="stat-section-title">Trends (last 30 days)</h3>
        <div className="dashboard-chart-grid">
          <div className="dashboard-chart-card">
            <h4>Orders per day</h4>
            <div className="dashboard-chart-inner">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={ordersChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebe6ea" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#8a8088" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#8a8088" allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" name="Orders" stroke="#3D2031" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="dashboard-chart-card">
            <h4>Paid revenue per day</h4>
            <div className="dashboard-chart-inner">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={revenueChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebe6ea" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#8a8088" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#8a8088" />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Line type="monotone" dataKey="amount" name="Revenue" stroke="#8A4D61" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="dashboard-chart-grid dashboard-chart-grid--half">
          <div className="dashboard-chart-card">
            <h4>Listings by status</h4>
            <div className="dashboard-chart-inner dashboard-chart-inner--pie">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={productStatusPie}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={2}
                  >
                    {productStatusPie.map((_, i) => (
                      <Cell key={`ps-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatNumber(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="dashboard-chart-card">
            <h4>Orders by status</h4>
            <div className="dashboard-chart-inner">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={orderStatusPie} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebe6ea" vertical={false} />
                  <XAxis dataKey="status" tick={{ fontSize: 11 }} stroke="#8a8088" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#8a8088" allowDecimals={false} />
                  <Tooltip formatter={(v) => formatNumber(v)} />
                  <Bar dataKey="count" name="Orders" radius={[6, 6, 0, 0]}>
                    {orderStatusPie.map((_, i) => (
                      <Cell key={`os-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {!isCloset ? (
        <div className="stat-section">
          <h3 className="stat-section-title">User overview</h3>
          <div className="stats-grid stats-grid--light">
            <div className="stat-card stat-card--light">
              <h3>Total users</h3>
              <div className="value">{formatNumber(stats.total_users)}</div>
              <div className="sub-value">Sellers, influencers, admins</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Total customers</h3>
              <div className="value">{formatNumber(stats.total_customers)}</div>
              <div className="sub-value">Buyers</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Active users</h3>
              <div className="value">{formatNumber(stats.active_users)}</div>
              <div className="sub-value">Not suspended</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Suspended users</h3>
              <div className="value">{formatNumber(stats.suspended_users)}</div>
              <div className="sub-value">Blocked accounts</div>
            </div>
          </div>
        </div>
      ) : null}

      {!isCloset ? (
        <div className="stat-section">
          <h3 className="stat-section-title">User types</h3>
          <div className="stats-grid stats-grid--light">
            <div className="stat-card stat-card--light">
              <h3>Influencers</h3>
              <div className="value">{formatNumber(stats.total_influencers)}</div>
              <div className="sub-value">Content creators</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Sellers</h3>
              <div className="value">{formatNumber(stats.total_sellers)}</div>
              <div className="sub-value">Product sellers</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="stat-section">
        <h3 className="stat-section-title">{isCloset ? 'Your listings' : 'Products overview'}</h3>
        <div className="stats-grid stats-grid--light">
          <div className="stat-card stat-card--light">
            <h3>Total listings</h3>
            <div className="value">{formatNumber(stats.total_products)}</div>
            <div className="sub-value">{isCloset ? 'Your closet' : 'All products'}</div>
          </div>
          {!isCloset ? (
            <>
              <div className="stat-card stat-card--light">
                <h3>Marketplace</h3>
                <div className="value">{formatNumber(stats.marketplace_products)}</div>
                <div className="sub-value">Marketplace items</div>
              </div>
              <div className="stat-card stat-card--light">
                <h3>Closet products</h3>
                <div className="value">{formatNumber(stats.closet_products)}</div>
                <div className="sub-value">Influencer closets</div>
              </div>
            </>
          ) : null}
          <div className="stat-card stat-card--light">
            <h3>Pending</h3>
            <div className="value">{formatNumber(stats.pending_products)}</div>
            <div className="sub-value">Awaiting approval</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Approved</h3>
            <div className="value">{formatNumber(stats.approved_products)}</div>
            <div className="sub-value">Live listings</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Sold</h3>
            <div className="value">{formatNumber(stats.sold_products)}</div>
            <div className="sub-value">Completed sales</div>
          </div>
        </div>
      </div>

      <div className="stat-section">
        <h3 className="stat-section-title">Orders overview</h3>
        <div className="stats-grid stats-grid--light">
          <div className="stat-card stat-card--light">
            <h3>Total orders</h3>
            <div className="value">{formatNumber(stats.total_orders)}</div>
            <div className="sub-value">{isCloset ? 'Your closet orders' : 'All orders'}</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Pending</h3>
            <div className="value">{formatNumber(stats.pending_orders)}</div>
            <div className="sub-value">Awaiting confirmation</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Confirmed</h3>
            <div className="value">{formatNumber(stats.confirmed_orders)}</div>
            <div className="sub-value">Processing</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Shipped</h3>
            <div className="value">{formatNumber(stats.shipped_orders)}</div>
            <div className="sub-value">In transit</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Delivered</h3>
            <div className="value">{formatNumber(stats.delivered_orders)}</div>
            <div className="sub-value">Completed</div>
          </div>
        </div>
      </div>

      <div className="stat-section">
        <h3 className="stat-section-title">Revenue overview</h3>
        <div className="stats-grid stats-grid--light">
          <div className="stat-card stat-card--light">
            <h3>Paid revenue</h3>
            <div className="value">{formatCurrency(stats.total_revenue)}</div>
            <div className="sub-value">Paid orders</div>
          </div>
          <div className="stat-card stat-card--light">
            <h3>Pending revenue</h3>
            <div className="value">{formatCurrency(stats.pending_revenue)}</div>
            <div className="sub-value">Awaiting payment</div>
          </div>
        </div>
      </div>

      {!isCloset ? (
        <div className="stat-section">
          <h3 className="stat-section-title">Platform activity</h3>
          <div className="stats-grid stats-grid--light">
            <div className="stat-card stat-card--light">
              <h3>Categories</h3>
              <div className="value">{formatNumber(stats.total_categories)}</div>
              <div className="sub-value">Product categories</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Active chats</h3>
              <div className="value">{formatNumber(stats.total_chats)}</div>
              <div className="sub-value">Conversations</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Wishlist items</h3>
              <div className="value">{formatNumber(stats.total_wishlist_items)}</div>
              <div className="sub-value">Saved products</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Product likes</h3>
              <div className="value">{formatNumber(stats.total_product_likes)}</div>
              <div className="sub-value">User interactions</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="stat-section">
          <h3 className="stat-section-title">Your engagement</h3>
          <div className="stats-grid stats-grid--light">
            <div className="stat-card stat-card--light">
              <h3>Buyer chats</h3>
              <div className="value">{formatNumber(stats.total_chats)}</div>
              <div className="sub-value">Where you are the seller</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Wishlist saves</h3>
              <div className="value">{formatNumber(stats.total_wishlist_items)}</div>
              <div className="sub-value">On your listings</div>
            </div>
            <div className="stat-card stat-card--light">
              <h3>Likes</h3>
              <div className="value">{formatNumber(stats.total_product_likes)}</div>
              <div className="sub-value">On your listings</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
